/**
 * TagPicker — multi-select avec autocomplete et creation inline.
 *
 *   <TagPicker
 *     value={selectedTags}
 *     onChange={setSelectedTags}
 *     onSearch={(q, kind) => api.tags.search(q, kind)}
 *     onCreate={(label, kind) => api.tags.create(label, kind)}  // optionnel
 *     kind="technique"  // optionnel : restreint a une taxonomie
 *   />
 *
 * Comportement :
 * - Chips listent les tags selectionnes avec ✕ pour retirer.
 * - Input pour taper ; Backspace sur input vide retire le dernier tag.
 * - Dropdown avec suggestions debounced 250ms via `onSearch`.
 * - Si la query exacte n'est pas dans les suggestions et `onCreate` fourni :
 *   bouton "Creer 'foo'" en fin de liste.
 * - Click hors du composant ferme le dropdown.
 * - ESC ferme aussi.
 *
 * S2.2 livre le composant pur ; S2.4 le branche sur `useTags()` (qui hit
 * `/api/tags?q=&kind=` dont l'autocomplete BE matche `label_fr` ET `label_en`
 * — la logique bilingue est cote serveur, le composant est neutre).
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import { Plus, X } from 'lucide-react'

import './TagPicker.css'

export type TagKind =
  'theme' | 'color' | 'technique' | 'mood' | 'subject' | 'format' | 'influence' | 'flag'

export interface Tag {
  id: number | string
  slug: string
  /** Label deja formate pour l'affichage (ex: `label_fr` pour l'UI atelier). */
  label: string
  kind?: TagKind
}

interface TagPickerProps {
  value: Tag[]
  onChange: (tags: Tag[]) => void
  /** Recherche debounce ; recoit la query + kind optionnel. */
  onSearch: (query: string, kind?: TagKind) => Promise<Tag[]>
  /** Si fourni, permet de creer un nouveau tag inline a partir du label tape. */
  onCreate?: (label: string, kind?: TagKind) => Promise<Tag>
  /** Restreint le picker a un kind unique (mode taxonomie fixee). */
  kind?: TagKind
  /** Limite max de tags selectionnables. */
  max?: number
  placeholder?: string
  ariaLabel?: string
  className?: string
  /** Texte du bouton "Creer". `{label}` est remplace par la query. */
  createLabel?: (query: string) => ReactNode
}

/**
 * Normalisation pour comparaison "label déjà utilisé" — strip accents +
 * casse + trim. Aligné avec la collation BE `utf8mb4_unicode_ci` (case +
 * accent insensible) qui pilote l'autocomplete `LIKE %q%`.
 */
function normalizeForMatch(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim()
    .toLowerCase()
}

const KIND_LABELS: Record<TagKind, string> = {
  theme: 'Thèmes',
  color: 'Couleurs',
  technique: 'Techniques',
  mood: 'Ambiances',
  subject: 'Sujets',
  format: 'Formats',
  influence: 'Influences',
  flag: 'Marqueurs',
}

export default function TagPicker({
  value,
  onChange,
  onSearch,
  onCreate,
  kind,
  max,
  placeholder = 'Ajouter un tag…',
  ariaLabel = 'Selecteur de tags',
  className,
  createLabel = (q) => (
    <>
      <Plus size={12} aria-hidden="true" /> Créer « {q} »
    </>
  ),
}: TagPickerProps) {
  const [query, setQuery] = useState('')
  const [suggestions, setSuggestions] = useState<Tag[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Click outside ferme le dropdown.
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Debounce search. `setLoading(true)` est place dans le callback du
  // setTimeout (donc asynchrone vs le corps d'effet, conforme a la regle
  // React 19 `set-state-in-effect`). On ne reset pas `suggestions` ici :
  // `filteredSuggestions` plus bas filtre deja sur la query courante.
  useEffect(() => {
    const trimmed = query.trim()
    if (!trimmed) return
    let cancelled = false
    const t = setTimeout(async () => {
      if (cancelled) return
      setLoading(true)
      try {
        const results = await onSearch(trimmed, kind)
        if (!cancelled) setSuggestions(results)
      } catch {
        if (!cancelled) setSuggestions([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [query, kind, onSearch])

  const selectedSlugs = useMemo(() => new Set(value.map((t) => t.slug)), [value])

  const filteredSuggestions = useMemo(
    () => (query.trim() ? suggestions.filter((s) => !selectedSlugs.has(s.slug)) : []),
    [query, suggestions, selectedSlugs],
  )

  // Aligne le check d'unicité côté FE sur la collation BE
  // `utf8mb4_unicode_ci` (case + accent insensible). Sinon "ecorce" propose
  // "Créer ecorce" alors que "Écorce" existe déjà.
  const normalizedQuery = normalizeForMatch(query)

  const exactMatch = filteredSuggestions.some((s) => normalizeForMatch(s.label) === normalizedQuery)

  const canCreate =
    !!onCreate &&
    query.trim().length > 0 &&
    !exactMatch &&
    !value.some((t) => normalizeForMatch(t.label) === normalizedQuery)

  const reachedMax = max !== undefined && value.length >= max

  const addTag = useCallback(
    (tag: Tag) => {
      if (selectedSlugs.has(tag.slug)) return
      if (reachedMax) return
      onChange([...value, tag])
      setQuery('')
      setSuggestions([])
      inputRef.current?.focus()
    },
    [onChange, reachedMax, selectedSlugs, value],
  )

  const removeTag = useCallback(
    (slug: string) => {
      onChange(value.filter((t) => t.slug !== slug))
    },
    [onChange, value],
  )

  const handleCreate = useCallback(async () => {
    if (!onCreate || !canCreate || creating || reachedMax) return
    setCreating(true)
    try {
      const created = await onCreate(query.trim(), kind)
      addTag(created)
    } catch {
      // L'erreur est remontee a l'appelant via le rejet de la promise ;
      // ici on ne fait rien d'autre que reset le loading state.
    } finally {
      setCreating(false)
    }
  }, [addTag, canCreate, creating, kind, onCreate, query, reachedMax])

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && query === '' && value.length > 0) {
      e.preventDefault()
      removeTag(value[value.length - 1].slug)
      return
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      if (filteredSuggestions.length > 0) {
        addTag(filteredSuggestions[0])
      } else if (canCreate) {
        void handleCreate()
      }
    }
  }

  // Groupement des suggestions par kind (utile en mode "tous kinds").
  const groupedSuggestions: Record<string, Tag[]> = {}
  for (const s of filteredSuggestions) {
    const k = s.kind ?? '_'
    groupedSuggestions[k] = groupedSuggestions[k] ?? []
    groupedSuggestions[k].push(s)
  }
  const groups = Object.entries(groupedSuggestions)

  const classes = ['tag-picker', className].filter(Boolean).join(' ')

  return (
    <div ref={containerRef} className={classes}>
      <div
        className="tag-picker-control"
        onClick={() => inputRef.current?.focus()}
        role="presentation"
      >
        {value.map((tag) => (
          <span key={tag.slug} className="tag-picker-chip">
            <span className="tag-picker-chip-label">{tag.label}</span>
            <button
              type="button"
              className="tag-picker-chip-remove"
              onClick={(e) => {
                e.stopPropagation()
                removeTag(tag.slug)
              }}
              aria-label={`Retirer ${tag.label}`}
            >
              <X size={12} aria-hidden="true" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          className="tag-picker-input"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={value.length === 0 ? placeholder : ''}
          aria-label={ariaLabel}
          disabled={reachedMax && query === ''}
        />
      </div>

      {open && (query.trim() || filteredSuggestions.length > 0) && (
        <div className="tag-picker-dropdown" role="listbox">
          {loading && <div className="tag-picker-hint">Recherche…</div>}
          {!loading && filteredSuggestions.length === 0 && !canCreate && (
            <div className="tag-picker-hint">Aucune correspondance</div>
          )}
          {groups.map(([k, items]) => (
            <div key={k} className="tag-picker-group">
              {k !== '_' && groups.length > 1 && (
                <div className="tag-picker-group-label">{KIND_LABELS[k as TagKind] ?? k}</div>
              )}
              {items.map((tag) => (
                <button
                  key={tag.slug}
                  type="button"
                  role="option"
                  aria-selected="false"
                  className="tag-picker-option"
                  onClick={() => addTag(tag)}
                  disabled={reachedMax}
                >
                  {tag.label}
                </button>
              ))}
            </div>
          ))}
          {canCreate && (
            <button
              type="button"
              className="tag-picker-create"
              onClick={() => void handleCreate()}
              disabled={creating}
            >
              {createLabel(query.trim())}
            </button>
          )}
        </div>
      )}

      {reachedMax && (
        <p className="tag-picker-max-notice">
          Maximum {max} tag{max && max > 1 ? 's' : ''} atteint.
        </p>
      )}
    </div>
  )
}
